import { Component, type ReactNode, type ErrorInfo } from "react";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 max-w-md text-center px-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-400 via-rose-500 to-pink-600" />
            <h2 className="text-lg font-semibold text-slate-200">Something went wrong</h2>
            <p className="text-sm text-slate-400">
              An unexpected error occurred. Please refresh the page. If the problem persists, contact support.
            </p>
            <button
              className="text-sm text-indigo-400 underline underline-offset-2"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
